import React, { useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { useTranslation } from "react-i18next";

import {
  ClientEngagement,
  CommercialProposalListItem,
  House,
  ProposalStatus,
} from "../../store/apis/commercialProposalApi";
import { HOUSE_LABEL } from "./ProjectsTable";

/** El orden de las columnas es el del recorrido de la propuesta. */
export const BOARD_COLUMNS: ProposalStatus[] = [
  "BACKLOG",
  "EN_PROCESO",
  "ENTREGADA",
  "ACEPTADA",
  "RECHAZADA",
];

/** Las dos finales llevan color para que el tablero se lea de un vistazo. */
const COLUMN_TONE: Partial<Record<ProposalStatus, "success" | "error">> = {
  ACEPTADA: "success",
  RECHAZADA: "error",
};

/**
 * El color de marca de cada house. Valores dados por negocio, no elegidos aquí.
 *
 * OJO con HOUSE_OF_MEDIA: #F7665C está a un pelo del color de error del tema (#FF6B79).
 * Por eso la línea de la house es una franja fina pegada al borde superior y nunca un
 * borde completo ni un fondo: así no se confunde con un aviso.
 *
 * Y el color NUNCA es el único canal: la línea lleva tooltip con los nombres.
 */
export const HOUSE_COLOR: Record<House, string> = {
  HOUSE_OF_MEDIA: "#F7665C",
  HOUSE_OF_AI: "#8FDECA",
  HOUSE_OF_STORIES: "#E38AD3",
  HOUSE_OF_DIGITAL: "#638CE1",
};

/** Los acortados que usa el equipo. Caben donde el nombre entero no. */
export const HOUSE_SHORT: Record<House, string> = {
  HOUSE_OF_MEDIA: "HoM",
  HOUSE_OF_AI: "HoAI",
  HOUSE_OF_STORIES: "HoS",
  HOUSE_OF_DIGITAL: "HoD",
};

/** Bandas de importe, del brief: verde hasta 25k, ámbar hasta 50k, naranja por encima. */
const BUDGET_BANDS: { limit: number; color: string; label: string }[] = [
  { limit: 25000, color: "#368548", label: "0 - 25.000 €" },
  { limit: 50000, color: "#C9A227", label: "25.001 - 50.000 €" },
  { limit: Infinity, color: "#E07A28", label: "+50.000 €" },
];

const bandFor = (amount: number) =>
  BUDGET_BANDS.find((band) => amount <= band.limit) ?? BUDGET_BANDS[2];

const euro = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const BUSINESS_UNIT_SHORT: Record<string, string> = {
  BEAPOLO: "BA",
  HOUSE_GROUP: "HG",
};

/** Iniciales de un nombre: "Ana Gil" -> "AG", "bruno@x.com" -> "BR". */
const initials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return (words[0] ?? "?").slice(0, 2).toUpperCase();
};

/**
 * Color estable a partir del nombre.
 *
 * Determinista a propósito: el mismo comercial tiene siempre el mismo color, en todas
 * las tarjetas y entre sesiones, que es lo que permite reconocerlo de un vistazo. Un
 * color aleatorio o por índice cambiaría al reordenar el listado.
 */
const avatarColor = (name: string): string => {
  const palette = [
    "#6579FC",
    "#368548",
    "#D946A0",
    "#E07A28",
    "#8B5CF6",
    "#0E7490",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % 100000;
  }
  return palette[hash % palette.length];
};

interface Props {
  items: CommercialProposalListItem[];
  onMove: (id: string, status: ProposalStatus) => void;
  /** Ids en vuelo, para no dejar arrastrar dos veces la misma tarjeta. */
  moving: Set<string>;
  onOpen: (id: string) => void;
}

/**
 * El tablero de propuestas.
 *
 * Drag & drop nativo del navegador, sin librería: siete columnas de tarjetas no justifican
 * meter una dependencia, y el repo no tenía ninguna.
 *
 * La tarjeta se mueve cuando el padre confirma, no al soltar: si el servidor rechaza, se
 * queda donde estaba en vez de saltar y volver.
 */
const ProposalBoard: React.FC<Props> = ({ items, onMove, moving, onOpen }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<ProposalStatus | null>(null);

  const handleDrop = (status: ProposalStatus) => {
    setOverColumn(null);
    const id = draggingId;
    setDraggingId(null);
    if (!id) return;
    const item = items.find((candidate) => candidate.id === id);
    // Soltarla en su propia columna no es un movimiento: evita una llamada y, sobre
    // todo, evita tocar la etapa del CRM sin motivo.
    if (!item || item.status === status) return;
    onMove(id, status);
  };

  /** La línea de color de arriba: un segmento por house, o gris si no hay ninguna. */
  const houseStripe = (houses: House[]) => {
    const label =
      houses.length > 0
        ? houses
            .map(
              (house) =>
                `${HOUSE_SHORT[house] ?? ""} · ${HOUSE_LABEL[house] ?? house}`
            )
            .join("  |  ")
        : t("commercialProposals.board.noHouse", "Sin house");

    return (
      <Tooltip title={label}>
        <Box
          sx={{
            display: "flex",
            height: 4,
            borderRadius: "4px 4px 0 0",
            overflow: "hidden",
            mb: 1,
          }}
        >
          {houses.length === 0 ? (
            <Box sx={{ flex: 1, backgroundColor: theme.palette.grey[300] }} />
          ) : (
            houses.map((house) => (
              <Box
                key={house}
                sx={{ flex: 1, backgroundColor: HOUSE_COLOR[house] }}
              />
            ))
          )}
        </Box>
      </Tooltip>
    );
  };

  const person = (name: string | null, role: string) => {
    if (!name) return null;
    return (
      <Tooltip key={role} title={`${role}: ${name}`}>
        <Avatar
          sx={{
            width: 24,
            height: 24,
            fontSize: 10,
            fontWeight: 700,
            bgcolor: avatarColor(name),
          }}
        >
          {initials(name)}
        </Avatar>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        // El scroll horizontal vive aqui y solo aqui: la cabecera con nueva propuesta,
        // el selector de vista y recargar tienen que quedarse siempre en pantalla.
        overflowX: "auto",
        minWidth: 0,
        // Un poco de aire para que la barra de scroll no tape la ultima fila de
        // tarjetas.
        pb: 1.5,
        alignItems: "flex-start",
      }}
    >
      {BOARD_COLUMNS.map((status) => {
        const columnItems = items.filter((item) => item.status === status);
        const tone = COLUMN_TONE[status];
        const isOver = overColumn === status;
        const total = columnItems.reduce(
          (sum, item) => sum + item.totalBudget,
          0
        );

        return (
          <Paper
            key={status}
            elevation={0}
            onDragOver={(event) => {
              // Sin el preventDefault el navegador no permite soltar.
              event.preventDefault();
              setOverColumn(status);
            }}
            onDragLeave={() => setOverColumn((c) => (c === status ? null : c))}
            onDrop={() => handleDrop(status)}
            sx={{
              minWidth: 268,
              width: 268,
              flexShrink: 0,
              borderRadius: 3,
              border: `1px solid ${
                isOver ? theme.palette.primary.main : theme.palette.grey[200]
              }`,
              backgroundColor: isOver
                ? theme.palette.action.hover
                : theme.palette.background.paper,
              p: 1.5,
              transition:
                "background-color 0.15s ease, border-color 0.15s ease",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography variant="subtitle2">
                {t(`commercialProposals.board.${status}`, status)}
              </Typography>
              <Chip
                size="small"
                color={tone ?? "default"}
                variant={tone ? "filled" : "outlined"}
                label={columnItems.length}
              />
            </Stack>

            {columnItems.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
              >
                {euro.format(total)}
              </Typography>
            )}

            <Stack gap={1}>
              {columnItems.map((item) => {
                const isMoving = moving.has(item.id);
                const band = bandFor(item.totalBudget);
                return (
                  <Paper
                    key={item.id}
                    elevation={0}
                    draggable={!isMoving}
                    onDragStart={() => setDraggingId(item.id)}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setOverColumn(null);
                    }}
                    onClick={() => onOpen(item.id)}
                    sx={{
                      border: `1px solid ${theme.palette.grey[200]}`,
                      borderRadius: 2,
                      p: 1.25,
                      pt: 0,
                      cursor: isMoving ? "wait" : "grab",
                      opacity: draggingId === item.id || isMoving ? 0.5 : 1,
                      backgroundColor: theme.palette.background.default,
                      overflow: "hidden",
                    }}
                  >
                    {/* La línea de la house, pegada al borde de arriba. */}
                    <Box sx={{ mx: -1.25 }}>{houseStripe(item.houses)}</Box>

                    {/* Importe y tipo de cliente: las dos etiquetas de cabecera. */}
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={0.5}
                      alignItems="center"
                      mb={0.75}
                      useFlexGap
                    >
                      <Tooltip title={band.label}>
                        <Chip
                          size="small"
                          label={euro.format(item.totalBudget)}
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#fff",
                            backgroundColor: band.color,
                          }}
                        />
                      </Tooltip>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(
                          `commercialProposals.engagement.${item.clientEngagement as ClientEngagement}`,
                          item.clientEngagement
                        )}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={0.5}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace", fontWeight: 700 }}
                      >
                        {item.proposalNumber}
                      </Typography>
                      {item.crmUrl && (
                        <Tooltip
                          title={t(
                            "commercialProposals.openInCrm",
                            "Abrir en el CRM"
                          )}
                        >
                          {/* stopPropagation: sin esto el clic abre también la ficha. */}
                          <a
                            href={item.crmUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            onDragStart={(event) => event.preventDefault()}
                            style={{
                              color: theme.palette.text.secondary,
                              display: "inline-flex",
                            }}
                          >
                            <LaunchIcon sx={{ fontSize: 14 }} />
                          </a>
                        </Tooltip>
                      )}
                    </Stack>

                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {item.brandName ?? item.clientName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.name}
                    </Typography>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {BUSINESS_UNIT_SHORT[item.businessUnit] ??
                          item.businessUnit}{" "}
                        · {item.deliveryDeadline}
                      </Typography>
                      {/* Los dos responsables. El de propuesta primero: es de quien
                          es la propuesta; el comercial detrás. */}
                      <Stack direction="row" spacing={-0.5}>
                        {person(
                          item.responsablePropuestaName,
                          t(
                            "commercialProposals.responsablePropuesta",
                            "Responsable de propuesta"
                          )
                        )}
                        {person(
                          item.responsableComercialName,
                          t(
                            "commercialProposals.responsableComercial",
                            "Responsable comercial"
                          )
                        )}
                      </Stack>
                    </Stack>

                    {item.crmStatus !== "SYNCED" && (
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ mt: 1, height: 20, fontSize: 11 }}
                        label={t(
                          "commercialProposals.board.notInCrm",
                          "sin enviar al CRM"
                        )}
                      />
                    )}
                  </Paper>
                );
              })}

              {columnItems.length === 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}
                >
                  {t("commercialProposals.board.empty", "Vacía")}
                </Typography>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Box>
  );
};

export default ProposalBoard;
